// Central data store for all services — used by Navbar, Services page, individual pages, SEO
export const services = [
  {
    slug: 'gst-registration-filing',
    title: 'GST Registration & Filing',
    shortTitle: 'GST',
    tagline: 'Stay compliant. Never miss a deadline.',
    icon: '🧾',
    color: '#00695C',
    gradient: 'from-teal-700 to-teal-500',
    metaTitle: 'GST Registration & Filing Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Expert GST registration and monthly/quarterly return filing services in Hyderabad. GSTR-1, GSTR-3B, GSTR-9, GST audit & compliance by experienced CAs.',
    overview: 'Goods and Services Tax (GST) is one of the most significant tax reforms in India. Our experts handle end-to-end GST compliance so you can focus on growing your business.',
    keyPoints: [
      'GST registration for new businesses within 7 working days',
      'GSTR-1, GSTR-3B, GSTR-9 return filing',
      'Input Tax Credit (ITC) reconciliation',
      'GST audit and assessment support',
      'LUT filing for exporters',
      'Cancellation and surrender of GSTIN',
    ],
    process: [
      { step: 1, title: 'Document Collection', desc: 'We collect PAN, Aadhaar, business proof, and bank details.' },
      { step: 2, title: 'Application Filing', desc: 'Our team prepares and files your GST registration application.' },
      { step: 3, title: 'ARN Generation', desc: 'Application Reference Number is generated within 24 hours.' },
      { step: 4, title: 'GSTIN Issued', desc: 'GSTIN certificate issued within 7 working days.' },
    ],
    faq: [
      { q: 'Who needs GST registration?', a: 'Any business with turnover above ₹40 lakh (goods) or ₹20 lakh (services) must register. E-commerce sellers and inter-state suppliers must also register regardless of turnover.' },
      { q: 'What documents are required?', a: 'PAN card, Aadhaar, proof of business address, bank account details, and digital signature (for companies/LLPs).' },
      { q: 'How long does GST registration take?', a: 'Typically 5–7 working days after submission of all documents without discrepancy.' },
    ],
  },
  {
    slug: 'income-tax-filing',
    title: 'Income Tax Filing',
    shortTitle: 'Income Tax',
    tagline: 'Accurate. Timely. Maximum savings.',
    icon: '📄',
    color: '#1A237E',
    gradient: 'from-blue-900 to-blue-700',
    metaTitle: 'Income Tax Return Filing Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Expert ITR filing for individuals, salaried employees, businesses, NRIs and companies. ITR-1 to ITR-7, tax planning, notices, and appeals handled by CAs in Hyderabad.',
    overview: 'Filing income tax returns accurately and on time protects you from penalties, enables loan processing, and ensures tax refunds are processed promptly.',
    keyPoints: [
      'ITR filing for individuals, HUFs, firms, companies (ITR-1 to ITR-7)',
      'Salary, business, capital gains, and rental income',
      'Tax planning and optimization strategies',
      'Response to IT notices and scrutiny assessments',
      'Form 15CA / 15CB for foreign remittances',
      '26AS reconciliation and TDS refund claims',
    ],
    process: [
      { step: 1, title: 'Income Summary', desc: 'Provide salary slips, Form-16, bank statements, and investment proofs.' },
      { step: 2, title: 'Tax Computation', desc: 'We compute your total income, applicable deductions, and tax liability.' },
      { step: 3, title: 'Review & Approval', desc: 'You review the draft ITR and approve before filing.' },
      { step: 4, title: 'Filing & Acknowledgment', desc: 'ITR is filed and acknowledgment (ITR-V) is sent to your email.' },
    ],
    faq: [
      { q: 'When is the deadline to file ITR?', a: 'For individuals: July 31 of the assessment year. For businesses requiring audit: October 31. Belated returns can be filed until December 31 with a late fee.' },
      { q: 'Can I file ITR if I missed the deadline?', a: 'Yes, belated returns can be filed with a late fee of ₹1,000–₹5,000 depending on income. However, you cannot carry forward certain losses.' },
      { q: 'What is Form-16?', a: 'Form-16 is a TDS certificate issued by your employer certifying the tax deducted from your salary. It is mandatory for salaried individuals.' },
    ],
  },
  {
    slug: 'company-llp-registration',
    title: 'LLP / Company Registration',
    shortTitle: 'Company Registration',
    tagline: 'Build a legal entity. Build credibility.',
    icon: '🏢',
    color: '#1565C0',
    gradient: 'from-blue-800 to-blue-600',
    metaTitle: 'Company & LLP Registration in Hyderabad | Finlytiq LLP',
    metaDesc: 'Register Private Limited Company, LLP, OPC, or Section 8 Company in Hyderabad. End-to-end incorporation support including DSC, DIN, MOA, AOA by experienced CAs.',
    overview: 'Choosing the right business structure is one of the most important decisions for any entrepreneur. We guide you through every step of incorporation.',
    keyPoints: [
      'Private Limited Company registration',
      'Limited Liability Partnership (LLP) registration',
      'One Person Company (OPC) registration',
      'Section 8 (NGO / Non-Profit) Company',
      'Director Identification Number (DIN) and DSC',
      'MOA, AOA, Partnership deed drafting',
    ],
    process: [
      { step: 1, title: 'Name Reservation', desc: 'We apply for company name availability and reservation via MCA portal.' },
      { step: 2, title: 'Document Preparation', desc: 'MOA, AOA, and incorporation documents are drafted and reviewed.' },
      { step: 3, title: 'MCA Filing', desc: 'SPICe+ or FiLLiP form is filed with MCA.' },
      { step: 4, title: 'Certificate of Incorporation', desc: 'COI, PAN, TAN issued digitally within 5–7 working days.' },
    ],
    faq: [
      { q: 'Private Limited vs LLP — which is better?', a: 'Private Limited is ideal for startups seeking venture capital and rapid scaling. LLP suits professional firms or partnerships with simpler compliance and lower costs. We guide you based on your business goals.' },
      { q: 'How long does company registration take?', a: 'Typically 7–10 working days from submission of complete documents.' },
      { q: 'What is the minimum capital required?', a: 'There is no minimum paid-up capital requirement. You can register a company with as little as ₹1.' },
    ],
  },
  {
    slug: 'roc-mca-compliance',
    title: 'ROC / MCA Compliance',
    shortTitle: 'ROC Compliance',
    tagline: 'Never miss a regulatory deadline again.',
    icon: '📋',
    color: '#4527A0',
    gradient: 'from-purple-900 to-purple-700',
    metaTitle: 'ROC / MCA Annual Compliance Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Complete ROC and MCA compliance for Private Limited Companies and LLPs. Annual returns, board meeting minutes, financial statements, and event-based filings.',
    overview: 'Every registered company and LLP must comply with mandatory annual filings under the Companies Act, 2013. Non-compliance attracts heavy penalties.',
    keyPoints: [
      'Annual return (MGT-7 / MGT-7A / Form 11) filing',
      'Financial statements (AOC-4 / Form 8) filing',
      'Board and shareholder meeting minutes',
      'Statutory registers maintenance',
      'Director KYC (DIR-3 KYC) filing',
      'Event-based filings (change of directors, address, share capital)',
    ],
    process: [
      { step: 1, title: 'Compliance Calendar', desc: 'We prepare a customized compliance calendar for your entity.' },
      { step: 2, title: 'Document Collation', desc: 'Financial statements, board resolutions, and registers are compiled.' },
      { step: 3, title: 'MCA Filing', desc: 'All forms are filed on time with digital signatures.' },
      { step: 4, title: 'Acknowledgment', desc: 'Filing receipts and SRN numbers are shared with you.' },
    ],
    faq: [
      { q: 'What happens if I miss ROC filing deadlines?', a: 'Late filing attracts additional fees of ₹100 per day per form, which can quickly accumulate. Prolonged non-compliance can result in director disqualification.' },
      { q: 'Which forms must a Private Limited file annually?', a: 'AOC-4 (financial statements) and MGT-7 (annual return) are mandatory. Additional forms depend on specific events during the year.' },
      { q: 'What is Director KYC?', a: 'DIR-3 KYC must be filed by every DIN holder annually by September 30. Failure results in DIN deactivation.' },
    ],
  },
  {
    slug: 'nri-taxation',
    title: 'NRI Taxation',
    shortTitle: 'NRI Taxation',
    tagline: 'Navigate Indian taxes from anywhere in the world.',
    icon: '✈️',
    color: '#B71C1C',
    gradient: 'from-red-900 to-red-700',
    metaTitle: 'NRI Tax Filing & Advisory Services in Hyderabad, India | Finlytiq LLP',
    metaDesc: 'Expert NRI taxation services from Hyderabad CAs: ITR filing, DTAA benefits, repatriation of funds, FEMA compliance, rental income tax, and NRO/NRE account advisory.',
    overview: 'Non-Resident Indians (NRIs) have unique tax obligations in India. From property rental income to capital gains on investments, we handle it all seamlessly.',
    keyPoints: [
      'NRI ITR filing (Indian-sourced income)',
      'DTAA (Double Taxation Avoidance Agreement) benefits',
      'Rental income and property sale tax computation',
      'Repatriation of funds from NRO to NRE accounts',
      'FEMA compliance and RBI permission filing',
      'Capital gains on shares, mutual funds, and property',
    ],
    process: [
      { step: 1, title: 'Residential Status', desc: 'We determine your residential status (NRI/RNOR/Resident) under Indian tax law.' },
      { step: 2, title: 'Income Identification', desc: 'All Indian-sourced income (rent, interest, dividends, capital gains) is identified.' },
      { step: 3, title: 'DTAA Assessment', desc: 'We evaluate applicable DTAA to minimize double taxation.' },
      { step: 4, title: 'Filing & Compliance', desc: 'ITR is filed with full disclosure and FEMA compliance ensured.' },
    ],
    faq: [
      { q: 'Do NRIs need to file ITR in India?', a: 'NRIs must file ITR if their Indian income exceeds ₹2.5 lakh per year. Even below this, filing is advisable for refund claims and financial record-keeping.' },
      { q: 'What is DTAA and how does it benefit NRIs?', a: 'Double Taxation Avoidance Agreements prevent the same income from being taxed in both India and your country of residence. We claim these benefits to minimize your tax outgo.' },
      { q: 'Can NRIs own property in India?', a: 'Yes, NRIs can own residential and commercial property in India, subject to certain FEMA restrictions on agricultural land.' },
    ],
  },
  {
    slug: 'accounting-bookkeeping',
    title: 'Accounting & Bookkeeping',
    shortTitle: 'Accounting',
    tagline: 'Know your numbers. Run your business.',
    icon: '📊',
    color: '#00695C',
    gradient: 'from-teal-900 to-teal-600',
    metaTitle: 'Accounting & Bookkeeping Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Professional accounting and bookkeeping services for SMEs, startups, and growing businesses in Hyderabad. Monthly MIS reports, P&L, balance sheets, and Tally/QuickBooks management.',
    overview: 'Accurate books are the foundation of any successful business. Our accounting team ensures your financials are always up-to-date, giving you real-time insight.',
    keyPoints: [
      'Monthly/quarterly bookkeeping and ledger maintenance',
      'Accounts payable and receivable management',
      'Bank reconciliation statements',
      'Profit & Loss and Balance Sheet preparation',
      'MIS reports for management decision-making',
      'Tally ERP, QuickBooks, Zoho Books management',
    ],
    process: [
      { step: 1, title: 'Software Setup', desc: 'We configure your accounting software and chart of accounts.' },
      { step: 2, title: 'Data Entry', desc: 'All transactions are recorded accurately on a weekly/monthly basis.' },
      { step: 3, title: 'Reconciliation', desc: 'Bank and ledger reconciliation performed monthly.' },
      { step: 4, title: 'MIS Reports', desc: 'Management reports delivered by the 10th of every month.' },
    ],
    faq: [
      { q: 'What software do you use?', a: 'We work with Tally ERP 9/Prime, QuickBooks, Zoho Books, and MS Excel. We can also work with your existing system.' },
      { q: 'How do I share documents?', a: 'You can share bills, invoices, and bank statements via WhatsApp, email, or shared cloud storage (Google Drive/Dropbox).' },
      { q: 'Is bookkeeping different from accounting?', a: 'Bookkeeping is the day-to-day recording of transactions. Accounting involves analysis, interpretation, and reporting of that data. We provide both.' },
    ],
  },
  {
    slug: 'startup-advisory',
    title: 'Startup Advisory',
    shortTitle: 'Startup Advisory',
    tagline: 'From idea to investor-ready.',
    icon: '💡',
    color: '#E65100',
    gradient: 'from-orange-800 to-orange-600',
    metaTitle: 'Startup Advisory Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'End-to-end startup advisory: company incorporation, DPIIT registration, fundraising support, pitch decks, ESOP structuring, and compliance roadmap for new businesses.',
    overview: 'Launching a startup involves complex legal, financial, and compliance decisions. We act as your strategic partner from day one.',
    keyPoints: [
      'Business entity selection and incorporation',
      'DPIIT Startup India registration',
      'Fundraising advisory and valuation support',
      'Investor-ready pitch deck preparation',
      'ESOP policy design and implementation',
      'Compliance roadmap for Series A-ready businesses',
    ],
    process: [
      { step: 1, title: 'Discovery', desc: 'We understand your business model, goals, and funding requirements.' },
      { step: 2, title: 'Structure', desc: 'Recommend optimal legal structure and tax-efficient setup.' },
      { step: 3, title: 'Launch', desc: 'Incorporation, PAN, TAN, GST, and bank account setup.' },
      { step: 4, title: 'Scale', desc: 'Ongoing compliance, MIS, and fundraising support.' },
    ],
    faq: [
      { q: 'What is DPIIT Startup India registration?', a: 'DPIIT recognition provides startups with tax exemptions, easier compliance, IPR fast-tracking, and eligibility for government funding schemes.' },
      { q: 'Do you help with fundraising?', a: 'Yes, we assist with financial modelling, investor pitch decks, due diligence preparation, and cap table structuring.' },
      { q: 'When should I engage a startup advisor?', a: 'Ideally from day one, before incorporating. Decisions about entity type, shareholding, and tax structure made early save significant time and money later.' },
    ],
  },
  {
    slug: 'virtual-cfo',
    title: 'Virtual CFO Services',
    shortTitle: 'Virtual CFO',
    tagline: 'CFO-level strategy. Fraction of the cost.',
    icon: '📈',
    color: '#1A237E',
    gradient: 'from-indigo-900 to-indigo-700',
    metaTitle: 'Virtual CFO Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Affordable Virtual CFO services for startups and SMEs in Hyderabad. Financial strategy, MIS reporting, investor relations, fundraising, budgeting, and cash flow management.',
    overview: 'A Virtual CFO provides enterprise-level financial leadership without the cost of a full-time CFO. Perfect for growing businesses that need strategic financial guidance.',
    keyPoints: [
      'Monthly financial reporting and MIS dashboard',
      'Budget preparation and variance analysis',
      'Cash flow forecasting and working capital management',
      'Fundraising support and investor communication',
      'KPI tracking and financial performance review',
      'Board-level financial presentations',
    ],
    process: [
      { step: 1, title: 'Financial Health Check', desc: 'Complete review of current financial position, systems, and processes.' },
      { step: 2, title: 'Strategy Design', desc: 'Custom financial strategy aligned with your business goals.' },
      { step: 3, title: 'Implementation', desc: 'Implement systems, reporting, and control frameworks.' },
      { step: 4, title: 'Ongoing Support', desc: 'Monthly reviews, board reports, and strategic guidance.' },
    ],
    faq: [
      { q: 'What is a Virtual CFO?', a: 'A Virtual CFO provides strategic financial leadership on a part-time or retainer basis. You get CFO expertise without the full-time salary cost of ₹30–60 lakh per year.' },
      { q: 'Who needs a Virtual CFO?', a: 'Startups post-Series A, SMEs with ₹5–100 Cr revenue, and businesses preparing for fundraising or acquisitions benefit most from VCFO services.' },
      { q: 'How is a VCFO different from an accountant?', a: 'An accountant records and reports historical data. A VCFO uses that data to drive forward-looking strategy, investor relations, and business growth.' },
    ],
  },
  {
    slug: 'business-consulting',
    title: 'Business Consulting',
    shortTitle: 'Consulting',
    tagline: 'Strategic clarity for complex decisions.',
    icon: '🎯',
    color: '#37474F',
    gradient: 'from-gray-800 to-gray-600',
    metaTitle: 'Business Consulting Services in Hyderabad | Finlytiq LLP',
    metaDesc: 'Strategic business consulting for SMEs and enterprises in Hyderabad. Business restructuring, due diligence, M&A advisory, process optimization, and growth strategy.',
    overview: 'Our consulting practice helps business leaders make better decisions, optimize operations, and drive sustainable growth through data-driven strategic advice.',
    keyPoints: [
      'Business restructuring and turnaround advisory',
      'Mergers, acquisitions, and due diligence',
      'Business valuation',
      'Process optimization and SOP development',
      'Financial modelling and scenario planning',
      'Government scheme identification and applications',
    ],
    process: [
      { step: 1, title: 'Situation Analysis', desc: 'Deep dive into your business: financials, operations, and market position.' },
      { step: 2, title: 'Gap Identification', desc: 'Identify key gaps, risks, and untapped opportunities.' },
      { step: 3, title: 'Strategy Development', desc: 'Co-create a prioritized action plan with measurable outcomes.' },
      { step: 4, title: 'Execution Support', desc: 'We stay involved to support implementation and course-correct as needed.' },
    ],
    faq: [
      { q: 'How is consulting different from accounting?', a: 'Accounting is a compliance function. Consulting is about strategy — how to grow, restructure, reduce costs, enter new markets, or prepare for an exit.' },
      { q: 'Can you help with government grants and schemes?', a: 'Yes. We identify applicable schemes (PLI, Startup India, MSME, SIDBI, etc.) and assist with applications and documentation.' },
      { q: 'Do you work with established businesses or only startups?', a: 'Both. Our consulting clients range from early-stage startups to established SMEs with revenues of ₹50–500 Cr.' },
    ],
  },
];

export const getServiceBySlug = (slug) => services.find(s => s.slug === slug);
